import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../config';

/**
 * Pricing page (BAB-70) — the surface paid-ads traffic lands on.
 *
 * Locked tier matrix (paywall bridge, Jun 2026): Free + Pro launch, Premium is a
 * waitlist until the tax layer ships. Pricing anchored to the income-ETF expense
 * ratio (not SaaS). Pro is the default/recommended anchor.
 *
 * Native to the app's dark system (matches the homepage --hp-* palette): cards
 * on #181818 with faint borders + light text, Pro set apart by a blue border /
 * glow / tint rather than by colour-inverting to white. Self-contained.
 */

const BG = '#131313';
const SURFACE = '#181818';
const LINE = 'rgba(255,255,255,0.08)';
const TEXT = '#f2f2f0';
const TEXT_DIM = '#b8b8b4';
const TEXT_MUTE = '#8a8a86';
const BRAND = '#2962ff';
const BRAND_HOVER = '#1e4bda';

interface Tier {
  name: string;
  tagline: string;
  price: string;
  cadence: string;
  frame: string;
  cta: string;
  ctaTo: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  badgeTone?: 'brand' | 'muted';
  disabled?: boolean;
}

const TIERS: Tier[] = [
  {
    name: 'Free',
    tagline: 'The Screener',
    price: '$0',
    cadence: 'forever',
    frame: 'See the gold. Find candidates and read the scores.',
    cta: 'Start free',
    ctaTo: '/register',
    features: [
      'Full screener + CC/CSP Score',
      'Public AI coach',
      'Read-only portfolio (up to 5 positions)',
      'A taste of AI analysis (a few runs / month)',
    ],
  },
  {
    name: 'Pro',
    tagline: 'The Manager',
    price: '$249',
    cadence: 'per year · $29/mo',
    frame: '≈0.1% of a $250k portfolio — vs the 0.35–0.68% a year an income ETF charges (JEPQ 0.35%, QQQI 0.68%) to cap your upside.',
    cta: 'Try Pro free for 14 days',
    ctaTo: '/register?plan=pro',
    highlighted: true,
    badge: 'Most popular',
    badgeTone: 'brand',
    features: [
      'Everything in Free, unlimited',
      'Real-Yield — "Is my income real?"',
      'Roll-vs-Assign decision card',
      'Benchmark lens (vs JEPQ / SCHD / SPY)',
      'Discipline engine + earnings guardrails',
      'Wheel tracking',
      'Unlimited AI analysis + AI daily emails',
    ],
  },
  {
    name: 'Premium',
    tagline: 'The Strategist',
    price: '$420',
    cadence: 'per year',
    frame: 'Tax-aware decisions + the deepest position-aware AI. Shipping soon.',
    cta: 'Join the waitlist',
    ctaTo: '/register',
    badge: 'Coming soon',
    badgeTone: 'muted',
    disabled: true,
    features: [
      'Everything in Pro',
      'Tax-aware flags (ROC, wash-sale, §1256)',
      'Account-type aware decisions',
      'Deepest position-aware AI coach',
    ],
  },
];

const PricingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistDone, setWaitlistDone] = useState(false);

  // Premium is displayed but unbuilt — turn the dead button into demand signal.
  // Reuses the existing public WTP-intent endpoint; tag with a campaign so the
  // premium-interest rows are easy to count. Email-only submissions are valid.
  const joinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    try {
      await fetch(`${config.API_URL}/api/wtp-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail.trim(), utm_campaign: 'premium_waitlist' }),
      });
    } catch {
      /* best-effort — still thank the user */
    }
    setWaitlistDone(true);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: BG }}>
      <style>{`
        @keyframes pp-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        .pp-card { animation: pp-rise .6s cubic-bezier(.2,.7,.2,1) both; transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; }
        .pp-card:hover { transform: translateY(-5px); border-color: rgba(255,255,255,0.16); }
        .pp-card-pro:hover { box-shadow: 0 26px 64px rgba(41,98,255,0.34); }
        .pp-cta { transition: transform .15s ease, box-shadow .25s ease, background .2s ease, border-color .2s ease; display: block; text-align: center; text-decoration: none; border-radius: 11px; font-weight: 650; }
        .pp-cta:hover { transform: translateY(-1px); }
        .pp-cta-pro:hover { background: ${BRAND_HOVER}; box-shadow: 0 12px 30px rgba(41,98,255,.45); }
        .pp-cta-ghost:hover { background: rgba(41,98,255,.10); border-color: ${BRAND}; }
        .pp-wl-input::placeholder { color: ${TEXT_MUTE}; }
        .pp-wl-input:focus { outline: none; border-color: ${BRAND}; box-shadow: 0 0 0 3px rgba(41,98,255,.22); }
        @media (max-width: 900px) { .pp-grid { flex-direction: column; align-items: center; } }
      `}</style>

      {/* atmosphere: soft brand glow up top + faint grid (matches homepage) */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(70% 42% at 50% -4%, rgba(41,98,255,0.16), transparent 68%)' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.6,
        background: 'linear-gradient(rgba(255,255,255,.022) 1px, transparent 1px)', backgroundSize: '100% 56px',
        maskImage: 'linear-gradient(180deg, #000, transparent 62%)', WebkitMaskImage: 'linear-gradient(180deg, #000, transparent 62%)' }} />

      <div style={{ position: 'relative', maxWidth: 1120, margin: '0 auto', padding: '76px 20px 100px' }}>
        {/* hero */}
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 2.5, color: BRAND, textTransform: 'uppercase' }}>
            Pricing
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 4.4vw, 44px)', lineHeight: 1.12, color: TEXT, margin: '14px 0 16px', fontWeight: 800, letterSpacing: -0.5 }}>
            Keep the upside JEPQ caps away.<br />
            <span style={{ color: BRAND }}>Know your income is real.</span>
          </h1>
          <p style={{ fontSize: 16.5, color: TEXT_DIM, lineHeight: 1.6, margin: 0 }}>
            Your ETF caps gains you should keep and calls your own money "income." Babylon runs the
            strategy yourself — with the engine that tells you when to roll, when to assign, and
            whether you're actually winning — for about 0.1% of your portfolio.
          </p>
        </div>

        {/* cards */}
        <div className="pp-grid" style={{ display: 'flex', gap: 22, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch', marginTop: 52 }}>
          {TIERS.map((t, i) => (
            <div
              key={t.name}
              className={`pp-card ${t.highlighted ? 'pp-card-pro' : ''}`}
              style={{
                animationDelay: `${i * 90}ms`,
                flex: '1 1 300px',
                maxWidth: 348,
                borderRadius: 18,
                padding: '30px 26px',
                background: t.highlighted
                  ? 'linear-gradient(180deg, rgba(41,98,255,0.10), rgba(41,98,255,0.02) 55%, transparent), #181818'
                  : SURFACE,
                border: t.highlighted ? `2px solid ${BRAND}` : `1px solid ${LINE}`,
                boxShadow: t.highlighted ? '0 22px 60px rgba(41,98,255,0.26)' : '0 8px 26px rgba(0,0,0,0.35)',
                transform: t.highlighted ? 'translateY(-8px)' : undefined,
                opacity: t.disabled ? 0.92 : 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {t.badge && (
                <span style={{
                  position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                  background: t.badgeTone === 'brand' ? BRAND : '#3a3a3a',
                  color: t.badgeTone === 'brand' ? '#fff' : TEXT_DIM,
                  fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, padding: '5px 13px',
                  borderRadius: 999, whiteSpace: 'nowrap',
                  border: t.badgeTone === 'brand' ? 'none' : `1px solid ${LINE}`,
                  boxShadow: t.badgeTone === 'brand' ? '0 6px 18px rgba(41,98,255,0.45)' : 'none',
                }}>
                  {t.badge}
                </span>
              )}

              <div style={{ fontSize: 12.5, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: 1 }}>
                {t.tagline}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, margin: '4px 0 12px' }}>{t.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 40, fontWeight: 850, color: TEXT, letterSpacing: -1 }}>{t.price}</span>
                <span style={{ fontSize: 13.5, color: TEXT_MUTE }}>{t.cadence}</span>
              </div>
              <p style={{ fontSize: 13.5, color: TEXT_DIM, lineHeight: 1.55, margin: '13px 0 20px', minHeight: 58 }}>{t.frame}</p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
                {t.features.map((f) => (
                  <li key={f} style={{ fontSize: 14, color: TEXT_DIM, padding: '7px 0', display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                    <span aria-hidden style={{
                      flex: '0 0 auto', marginTop: 1, width: 17, height: 17, borderRadius: 999,
                      background: 'rgba(41,98,255,0.16)', color: '#6f9bff', fontSize: 11, fontWeight: 800,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {t.disabled ? (
                waitlistDone ? (
                  <span style={{ color: '#34d399', fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                    ✓ You're on the list — we'll email you when it ships.
                  </span>
                ) : (
                  <form onSubmit={joinWaitlist} style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    <input
                      className="pp-wl-input"
                      type="email"
                      required
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      placeholder="you@email.com"
                      aria-label="Email for the Premium waitlist"
                      style={{ padding: '11px 13px', borderRadius: 10, border: `1px solid ${LINE}`, background: BG, color: TEXT, fontSize: 14, transition: 'border-color .15s, box-shadow .15s' }}
                    />
                    <button type="submit" className="pp-cta pp-cta-ghost" style={{ background: 'transparent', color: BRAND, border: `1.5px solid ${BRAND}`, padding: '11px 16px', fontSize: 15, cursor: 'pointer' }}>
                      {t.cta}
                    </button>
                  </form>
                )
              ) : (
                <Link
                  to={isAuthenticated && t.name === 'Free' ? '/dashboard' : t.ctaTo}
                  className={`pp-cta ${t.highlighted ? 'pp-cta-pro' : 'pp-cta-ghost'}`}
                  style={{
                    background: t.highlighted ? BRAND : 'transparent',
                    color: t.highlighted ? '#fff' : BRAND,
                    border: t.highlighted ? '1.5px solid transparent' : `1.5px solid ${BRAND}`,
                    padding: '13px 16px',
                    fontSize: 15,
                    boxShadow: t.highlighted ? '0 8px 22px rgba(41,98,255,0.3)' : 'none',
                  }}
                >
                  {t.cta}
                </Link>
              )}
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', color: TEXT_MUTE, fontSize: 13, marginTop: 38 }}>
          Cancel anytime · Pro trial needs no card up front · Not financial advice.
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
